import { useMemo } from 'react';
import i18n from 'Utilities/i18n';
import * as z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

type Step = 'initialAccount' | 'adultAccount' | 'minorInfo' | 'minorAccount';

export const useNewUserValidationSchema = ({ step }: { step: number }) => {
  const validationSchema = useMemo(() => {
    const initialAccount = {
      firstName: z
        .string(
          i18n.t(
            'CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.firstName.errors.required'
          )
        )
        .min(
          1,
          i18n.t(
            'CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.firstName.errors.required'
          )
        )
        .trim(),
      lastName: z
        .string(
          i18n.t(
            'CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.lastName.errors.required'
          )
        )
        .min(
          1,
          i18n.t(
            'CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.lastName.errors.required'
          )
        )
        .trim(),
      dateOfBirth: z.iso.date(
        i18n.t(
          'CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.dob.errors.required'
        )
      ),
    };

    const adultAccount = {
      email: z
        .string(
          i18n.t(
            'CreateAccountAccountInfo.content.createAccountAccountInfoForm.formField.email.errors.required'
          )
        )
        .min(
          1,
          i18n.t(
            'CreateAccountAccountInfo.content.createAccountAccountInfoForm.formField.email.errors.required'
          )
        )
        .email({
          abort: false,
          error: i18n.t(
            'CreateAccountAccountInfo.content.createAccountAccountInfoForm.formField.email.errors.emailAddressValidEmail'
          ),
        }),
      password: z
        .string(
          // PasswordInputValidator handles showing the password requirements
          ''
        )
        .min(
          1,
          // PasswordInputValidator handles showing the password requirements
          ''
        ),
      confirmPassword: z
        .string(
          i18n.t(
            'CreateAccountAccountInfo.content.createAccountAccountInfoForm.formField.confirmPassword.errors.required'
          )
        )
        .min(
          1,
          i18n.t(
            'CreateAccountAccountInfo.content.createAccountAccountInfoForm.formField.confirmPassword.errors.required'
          )
        ),
    };

    const minorAccount = {
      guardianFirstName: z
        .string(
          i18n.t(
            'CreateAccountPersonalInfo.content.createAccountPersonalInfoFormMinor.formField.parentFirstName.helpText.required'
          )
        )
        .min(
          1,
          i18n.t(
            'CreateAccountPersonalInfo.content.createAccountPersonalInfoFormMinor.formField.parentFirstName.helpText.required'
          )
        )
        .trim(),
      guardianLastName: z
        .string(
          i18n.t(
            'CreateAccountPersonalInfo.content.createAccountPersonalInfoFormMinor.formField.parentLastName.helpText.required'
          )
        )
        .min(
          1,
          i18n.t(
            'CreateAccountPersonalInfo.content.createAccountPersonalInfoFormMinor.formField.parentLastName.helpText.required'
          )
        )
        .trim(),
    };

    const schemaStep: Step =
      step === 0
        ? 'initialAccount'
        : step === 1 || step === 2
          ? 'adultAccount'
          : step === 3
            ? 'minorInfo'
            : 'minorAccount';

    let schema;

    switch (schemaStep) {
      // Note: including matching password validation only for schemas containing "adultAccount"

      case 'adultAccount':
        schema = z
          .object({ ...initialAccount, ...adultAccount })

          .refine((data) => data.password === data.confirmPassword, {
            error: i18n.t(
              'CreateAccountAccountInfo.content.createAccountAccountInfoForm.formField.confirmPassword.errors.confirmPasswordMustMatch'
            ),
            path: ['confirmPassword'],
          });
        break;
      case 'minorInfo':
        schema = z.object({ ...initialAccount, ...minorAccount });
        break;
      case 'minorAccount':
        schema = z
          .object({ ...initialAccount, ...adultAccount, ...minorAccount })
          .refine((data) => data.password === data.confirmPassword, {
            error: i18n.t(
              'CreateAccountAccountInfo.content.createAccountAccountInfoForm.formField.confirmPassword.errors.confirmPasswordMustMatch'
            ),
            path: ['confirmPassword'],
          });
        break;
      default:
        schema = z.object(initialAccount);
        break;
    }

    return toFormikValidationSchema(schema);
  }, [step]);

  return validationSchema;
};
